/**
 * RdsTopologyView — Remote Desktop Services farm.
 *
 * Modelo:
 *   {
 *     rds: {
 *       mode: 'standalone' | 'farm_ha',
 *       gateway: { server_id, ha, fqdn, certificate, notes },
 *       web_access: { server_id, ha, url, notes },
 *       broker: { server_id, ha, partner_id, notes },
 *       session_hosts: [{ name, server_id, max_sessions, role ('rdsh'|'rdvh'), os, notes }],
 *       resources: {
 *         file_server_id, sql_server_id, ad_service_id, license_server_id,
 *         fslogix_share, license_mode ('per_user'|'per_device'),
 *       },
 *       access: { mfa, vpn_required, internet_published },
 *       monitoring: { enabled, tools: ['SIEM', 'WSUS', 'Email/SMS Alerts', ...] },
 *       features: [...],
 *     },
 *     notes: '...',
 *   }
 */
import React from 'react';
import { ShieldCheckIcon, GlobeAltIcon, Squares2X2Icon, ComputerDesktopIcon, FolderOpenIcon, CircleStackIcon, KeyIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import {
  useTopologyState, useTopologyCandidates, TopologyHeader,
  SectionBox, NodeCard, ListSection,
  TOPOLOGY_PALETTE, serverLabel,
} from './TopologyShared';

const DEFAULT = {
  rds: {
    mode: 'farm_ha',
    gateway:    { server_id: null, ha: false, fqdn: '', certificate: '', notes: '' },
    web_access: { server_id: null, ha: false, url: '', notes: '' },
    broker:     { server_id: null, ha: false, partner_id: null, notes: '' },
    session_hosts: [],
    resources: {
      file_server_id: null, sql_server_id: null, ad_service_id: null, license_server_id: null,
      fslogix_share: '', license_mode: 'per_user',
    },
    access: { mfa: false, vpn_required: false, internet_published: false },
    monitoring: { enabled: false, tools: [] },
    features: [],
  },
  notes: '',
};

const MODES = [
  { value: 'standalone', label: 'Standalone (un único host)' },
  { value: 'farm_ha',    label: 'Granja HA (gateway + broker + session hosts)' },
];

const LICENSE_MODES = [
  { value: 'per_user',   label: 'Per User (RDS CALs por usuario)' },
  { value: 'per_device', label: 'Per Device (RDS CALs por dispositivo)' },
];

export default function RdsTopologyView({ serviceId, companyId, serviceName }) {
  const state = useTopologyState(serviceId, companyId, DEFAULT);
  const { servers, locations } = useTopologyCandidates(serviceId, companyId);
  const { topology, setTopology, editMode, setEditMode,
          saving, error, loading, save, reload } = state;

  if (loading) return <div className="dv-tab-pane">Cargando topología RDS…</div>;
  if (error)   return <div className="dv-tab-pane" style={{ color: '#f87171' }}>{error}</div>;

  const rds = topology.rds || DEFAULT.rds;
  const set    = (k, v)         => setTopology(t => ({ ...t, rds: { ...t.rds, [k]: v } }));
  const setObj = (key, k, v)    => setTopology(t => ({ ...t, rds: { ...t.rds, [key]: { ...(t.rds?.[key] || {}), [k]: v } } }));

  const isHA = rds.mode === 'farm_ha';
  const accent = isHA ? TOPOLOGY_PALETTE.haAccent : TOPOLOGY_PALETTE.primaryAccent;

  const renderRoleCard = (cfg, defs) => (
    <NodeCard
      icon={defs.icon}
      accent={defs.accent}
      title={defs.title}
      subtitle={cfg?.server_id ? serverLabel(cfg.server_id, servers) : <em>Sin servidor asignado</em>}
      badges={[
        ...(cfg?.ha ? [{ label: 'HA', bg: 'rgba(245,158,11,.18)', fg: '#fbbf24' }] : []),
      ]}
      footer={
        <>
          {cfg?.fqdn        && <div>FQDN: {cfg.fqdn}</div>}
          {cfg?.url         && <div>URL: {cfg.url}</div>}
          {cfg?.certificate && <div>Cert: {cfg.certificate}</div>}
        </>
      }
    />
  );

  return (
    <div className="dv-tab-pane" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TopologyHeader
        title={serviceName || 'RDS Farm'}
        subtitle="Granja de Remote Desktop Services"
        editMode={editMode}
        saving={saving}
        onEdit={() => setEditMode(true)}
        onCancel={() => { setEditMode(false); reload(); }}
        onSave={save}
        onReload={reload}
      />

      <SectionBox title="Modo de despliegue" accent={accent}>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Modo</label>
            {editMode ? (
              <select className="form-select form-select-sm" value={rds.mode}
                      onChange={e => set('mode', e.target.value)}>
                {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            ) : (
              <div style={{ color: TOPOLOGY_PALETTE.textStrong }}>
                {MODES.find(m => m.value === rds.mode)?.label || rds.mode}
              </div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">Acceso</label>
            {editMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6 }}>
                  <input type="checkbox" className="form-check-input" checked={!!rds.access?.mfa}
                         onChange={e => setObj('access', 'mfa', e.target.checked)} />
                  <span>MFA obligatorio</span>
                </label>
                <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6 }}>
                  <input type="checkbox" className="form-check-input" checked={!!rds.access?.vpn_required}
                         onChange={e => setObj('access', 'vpn_required', e.target.checked)} />
                  <span>Requiere VPN previa</span>
                </label>
                <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6 }}>
                  <input type="checkbox" className="form-check-input" checked={!!rds.access?.internet_published}
                         onChange={e => setObj('access', 'internet_published', e.target.checked)} />
                  <span>Publicado a Internet</span>
                </label>
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {rds.access?.mfa && <span style={{ background: 'rgba(16,185,129,.18)', color: '#6ee7b7', borderRadius: 4, padding: '2px 7px', fontSize: '0.75rem', fontWeight: 700 }}>MFA</span>}
                {rds.access?.vpn_required && <span style={{ background: 'rgba(99,102,241,.18)', color: '#a5b4fc', borderRadius: 4, padding: '2px 7px', fontSize: '0.75rem', fontWeight: 700 }}>VPN</span>}
                {rds.access?.internet_published && <span style={{ background: 'rgba(245,158,11,.18)', color: '#fbbf24', borderRadius: 4, padding: '2px 7px', fontSize: '0.75rem', fontWeight: 700 }}>Internet</span>}
                {!rds.access?.mfa && !rds.access?.vpn_required && !rds.access?.internet_published && <em style={{ color: TOPOLOGY_PALETTE.textMuted }}>Sin acceso configurado</em>}
              </div>
            )}
          </div>
        </div>
      </SectionBox>

      {/* ── Diagrama ── */}
      {!editMode && (
        <SectionBox title="Diagrama de la granja" accent={accent}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {renderRoleCard(rds.gateway,    { icon: ShieldCheckIcon, accent: TOPOLOGY_PALETTE.haAccent,        title: 'RD Gateway' })}
              {renderRoleCard(rds.web_access, { icon: GlobeAltIcon,    accent: TOPOLOGY_PALETTE.primaryAccent,   title: 'RD Web Access' })}
              {renderRoleCard(rds.broker,     { icon: Squares2X2Icon,  accent: TOPOLOGY_PALETTE.secondaryAccent, title: 'Connection Broker' })}
            </div>

            <div>
              <div style={{ fontSize: '0.74rem', fontWeight: 700, color: TOPOLOGY_PALETTE.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Session Hosts ({(rds.session_hosts || []).length})
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {(rds.session_hosts || []).length === 0 && (
                  <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>Sin session hosts.</div>
                )}
                {(rds.session_hosts || []).map((h, i) => (
                  <NodeCard
                    key={i}
                    icon={ComputerDesktopIcon}
                    accent={TOPOLOGY_PALETTE.successAccent}
                    title={h.name || (h.server_id ? serverLabel(h.server_id, servers) : `RDSH-${String(i+1).padStart(2,'0')}`)}
                    subtitle={h.os || 'Windows Server'}
                    badges={h.max_sessions ? [{ label: `${h.max_sessions} sesiones`, bg: 'rgba(99,102,241,.18)', fg: '#a5b4fc' }] : []}
                    footer={h.server_id && h.name ? <div>{serverLabel(h.server_id, servers)}</div> : null}
                  />
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.74rem', fontWeight: 700, color: TOPOLOGY_PALETTE.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Recursos y servicios
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {rds.resources?.file_server_id && (
                  <NodeCard icon={FolderOpenIcon} accent={TOPOLOGY_PALETTE.secondaryAccent}
                    title="File Server (FSLogix)"
                    subtitle={serverLabel(rds.resources.file_server_id, servers)}
                    footer={rds.resources.fslogix_share ? <div>Share: {rds.resources.fslogix_share}</div> : null}
                  />
                )}
                {rds.resources?.sql_server_id && (
                  <NodeCard icon={CircleStackIcon} accent={TOPOLOGY_PALETTE.primaryAccent}
                    title="SQL Server (Broker DB)"
                    subtitle={serverLabel(rds.resources.sql_server_id, servers)}
                  />
                )}
                {rds.resources?.ad_service_id && (
                  <NodeCard icon={UserGroupIcon} accent={TOPOLOGY_PALETTE.haAccent}
                    title="Active Directory"
                    subtitle={`Service #${rds.resources.ad_service_id}`}
                    footer={<div>Autenticación / GPOs</div>}
                  />
                )}
                {rds.resources?.license_server_id && (
                  <NodeCard icon={KeyIcon} accent="#a78bfa"
                    title="License Server (RDS CALs)"
                    subtitle={serverLabel(rds.resources.license_server_id, servers)}
                    footer={<div>{LICENSE_MODES.find(m => m.value === rds.resources?.license_mode)?.label}</div>}
                  />
                )}
                {!rds.resources?.file_server_id && !rds.resources?.sql_server_id && !rds.resources?.ad_service_id && !rds.resources?.license_server_id && (
                  <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>Sin recursos asociados.</div>
                )}
              </div>
            </div>
          </div>
        </SectionBox>
      )}

      {/* ── Roles ── */}
      {editMode && (
        <>
          {[
            ['gateway',    'RD Gateway',         { fqdn: true, certificate: true }],
            ['web_access', 'RD Web Access',      { url: true }],
            ['broker',     'Connection Broker',  {}],
          ].map(([key, title, fields]) => (
            <SectionBox key={key} title={title} accent={accent}>
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="form-label">Servidor</label>
                  <select className="form-select form-select-sm" value={rds[key]?.server_id || ''}
                          onChange={e => setObj(key, 'server_id', e.target.value ? Number(e.target.value) : null)}>
                    <option value="">—</option>
                    {(servers || []).map(s => (
                      <option key={`${s.kind}-${s.id}`} value={s.id}>{s.hostname || s.name}{s.ip ? ` · ${s.ip}` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6, marginTop: 24 }}>
                    <input type="checkbox" className="form-check-input" checked={!!rds[key]?.ha}
                           onChange={e => setObj(key, 'ha', e.target.checked)} />
                    <span>HA</span>
                  </label>
                </div>
                {fields.fqdn && (
                  <div className="col-md-5">
                    <label className="form-label">FQDN público</label>
                    <input className="form-control form-control-sm" value={rds[key]?.fqdn || ''}
                           onChange={e => setObj(key, 'fqdn', e.target.value)} />
                  </div>
                )}
                {fields.certificate && (
                  <div className="col-md-12">
                    <label className="form-label">Certificado SSL</label>
                    <input className="form-control form-control-sm" placeholder="CN=rdsgw.empresa.com (Let's Encrypt / wildcard)"
                           value={rds[key]?.certificate || ''}
                           onChange={e => setObj(key, 'certificate', e.target.value)} />
                  </div>
                )}
                {fields.url && (
                  <div className="col-md-5">
                    <label className="form-label">URL portal</label>
                    <input className="form-control form-control-sm" placeholder="https://rdsweb.empresa.com/RDWeb"
                           value={rds[key]?.url || ''}
                           onChange={e => setObj(key, 'url', e.target.value)} />
                  </div>
                )}
              </div>
            </SectionBox>
          ))}
        </>
      )}

      {/* ── Session hosts ── */}
      <ListSection
        title="Session Hosts (RDSH Farm)"
        accent={TOPOLOGY_PALETTE.successAccent}
        items={rds.session_hosts}
        onChange={(v) => set('session_hosts', v)}
        editMode={editMode}
        servers={servers}
        locations={locations}
        defaultRow={{ role: 'rdsh', os: 'Windows Server' }}
        columns={[
          { key: 'name',         label: 'Nombre',     placeholder: 'RDSH-01' },
          { key: 'server_id',    label: 'Servidor',   type: 'server' },
          { key: 'os',           label: 'SO',         placeholder: 'Windows Server 2022' },
          { key: 'max_sessions', label: 'Sesiones',   type: 'number' },
          { key: 'role',         label: 'Rol',        type: 'select', options: [{ value: 'rdsh', label: 'RDSH (Session Host)' }, { value: 'rdvh', label: 'RDVH (VDI host)' }] },
          { key: 'notes',        label: 'Notas' },
        ]}
        addLabel="Añadir session host"
        emptyText="Sin session hosts."
      />

      {/* ── Recursos ── */}
      <SectionBox title="Recursos y servicios asociados" accent={TOPOLOGY_PALETTE.secondaryAccent}>
        {editMode ? (
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">File Server (FSLogix)</label>
              <select className="form-select form-select-sm" value={rds.resources?.file_server_id || ''}
                      onChange={e => setObj('resources', 'file_server_id', e.target.value ? Number(e.target.value) : null)}>
                <option value="">—</option>
                {(servers || []).map(s => <option key={`${s.kind}-${s.id}`} value={s.id}>{s.hostname || s.name}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Share FSLogix</label>
              <input className="form-control form-control-sm" placeholder="\\\\fileserver\\fslogix$"
                     value={rds.resources?.fslogix_share || ''}
                     onChange={e => setObj('resources', 'fslogix_share', e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label">SQL Server (Broker DB)</label>
              <select className="form-select form-select-sm" value={rds.resources?.sql_server_id || ''}
                      onChange={e => setObj('resources', 'sql_server_id', e.target.value ? Number(e.target.value) : null)}>
                <option value="">—</option>
                {(servers || []).map(s => <option key={`${s.kind}-${s.id}`} value={s.id}>{s.hostname || s.name}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">License Server</label>
              <select className="form-select form-select-sm" value={rds.resources?.license_server_id || ''}
                      onChange={e => setObj('resources', 'license_server_id', e.target.value ? Number(e.target.value) : null)}>
                <option value="">—</option>
                {(servers || []).map(s => <option key={`${s.kind}-${s.id}`} value={s.id}>{s.hostname || s.name}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Modo licencias</label>
              <select className="form-select form-select-sm" value={rds.resources?.license_mode || 'per_user'}
                      onChange={e => setObj('resources', 'license_mode', e.target.value)}>
                {LICENSE_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Servicio AD asociado (id)</label>
              <input type="number" className="form-control form-control-sm"
                     placeholder="ID del servicio AD"
                     value={rds.resources?.ad_service_id || ''}
                     onChange={e => setObj('resources', 'ad_service_id', e.target.value === '' ? null : Number(e.target.value))} />
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textStrong }}>
            {rds.resources?.file_server_id    && <div>File Server: {serverLabel(rds.resources.file_server_id, servers)} {rds.resources.fslogix_share && `(${rds.resources.fslogix_share})`}</div>}
            {rds.resources?.sql_server_id     && <div>SQL Server: {serverLabel(rds.resources.sql_server_id, servers)}</div>}
            {rds.resources?.license_server_id && <div>License Server: {serverLabel(rds.resources.license_server_id, servers)} · {LICENSE_MODES.find(m => m.value === rds.resources?.license_mode)?.label}</div>}
            {rds.resources?.ad_service_id     && <div>AD Service #{rds.resources.ad_service_id}</div>}
          </div>
        )}
      </SectionBox>

      {/* ── Monitorización ── */}
      <SectionBox title="Monitorización y gestión" accent={TOPOLOGY_PALETTE.successAccent}>
        {editMode ? (
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6, marginTop: 24 }}>
                <input type="checkbox" className="form-check-input" checked={!!rds.monitoring?.enabled}
                       onChange={e => setObj('monitoring', 'enabled', e.target.checked)} />
                <span>Monitorización activa</span>
              </label>
            </div>
            <div className="col-md-9">
              <label className="form-label">Herramientas (una por línea)</label>
              <textarea className="form-control" rows={3}
                placeholder="SIEM (Syslog) / Email-SMS Alerts / WSUS / Backups VEEAM…"
                value={(rds.monitoring?.tools || []).join('\n')}
                onChange={e => setObj('monitoring', 'tools', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))} />
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textStrong }}>
            {rds.monitoring?.enabled
              ? <>Activa{(rds.monitoring.tools || []).length > 0 && <> · {rds.monitoring.tools.join(', ')}</>}</>
              : <em style={{ color: TOPOLOGY_PALETTE.textMuted }}>Sin monitorización documentada.</em>}
          </div>
        )}
      </SectionBox>

      {/* ── Features ── */}
      <SectionBox title="Características clave" accent={TOPOLOGY_PALETTE.primaryAccent}>
        {editMode ? (
          <textarea className="form-control" rows={4}
            placeholder="Una característica por línea (ej. Single Sign-On con AD, Escalabilidad horizontal, Perfiles FSLogix centralizados…)"
            value={(rds.features || []).join('\n')}
            onChange={e => set('features', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
          />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(rds.features || []).length === 0 && (
              <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>Sin características.</div>
            )}
            {(rds.features || []).map((f, i) => (
              <span key={i} style={{ background: 'rgba(59,130,246,.12)', color: '#93c5fd', border: '1px solid rgba(59,130,246,.35)', padding: '3px 8px', borderRadius: 12, fontSize: '0.78rem' }}>{f}</span>
            ))}
          </div>
        )}
      </SectionBox>
    </div>
  );
}
